/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * EosTransferMediaExistingFtpsModal — List/select/transfer existing FTP studies.
 * Migrated from: lego/page/eos/transfer-media/EosTransferMediaExistingFtpsModal.jsx
 */

import { useState, useEffect, useRef } from 'react'
import { Button } from 'react-bootstrap'
import { sortBy } from 'lodash'
import { useServiceContext } from '../../../root'
import { useModal } from '../../../contexts/ModalContext'
import Modal from '../../../components/Modal'
import FilterTrigger from '../../../components/FilterTrigger'
import EosStudyInfoDialog from './EosStudyInfoDialog'
import TableMenu from './TableMenu'
import EosTransferMediaStudyStatusFilter from './EosTransferMediaStudyStatusFilter'
import type {
  EosTransferMediaExistingFtpsModalProps,
  FtpContext,
  SortState,
  TableHeader,
  StudyStateFilter
} from './types'
import { STUDY_STATES } from './types'

const PDF_GEN_SUBSTRINGS = ['s3://', 's3.amazonaws']

const EosTransferMediaExistingFtpsModal: React.FC<EosTransferMediaExistingFtpsModalProps> = ({ isPdrFlow }) => {
  const context = useServiceContext()
  const { showModal } = useModal()
  const { translate } = context.service.filter
  const [isOpen, setIsOpen] = useState(true)
  const [sort, setSort] = useState<SortState>({ descending: true, column: 'name' })
  const [ftpData, setFtpData] = useState<FtpContext[] | null>(null)
  const [filteredFtpData, setFilteredFtpData] = useState<FtpContext[]>([])
  const [selectedFtpId, setSelectedFtpId] = useState<number | null>(null)
  const [removedFtpId, setRemovedFtpId] = useState<number | null>(null)
  const [studyStateFilter, setStudyStateFilter] = useState<StudyStateFilter>('ACTIVE')
  const [selectedFtpData, setSelectedFtpData] = useState<Partial<FtpContext>>({})
  const [userRole, setUserRole] = useState<string | null>(null)
  const sortRef = useRef<SortState>({ descending: true, column: 'name' })

  const headers: TableHeader[] = [
    { column: 'name', text: 'NAME' },
    { column: 'remoteFolderName', text: 'EOS_MEDIA_STUDY_LEVEL_FOLDER_PATH' },
    {
      column: 'targetFolderName',
      text: isPdrFlow ? 'FTP_FORM_LABEL_PDR_DESTINATION_FOLDER' : 'FTP_FORM_LABEL_RCM_DESTINATION_FOLDER'
    }
  ]

  useEffect(() => {
    context.api.settings.getFtpContextsForPicker(true).then((_data) => {
      const data = _data as FtpContext[]
      const ftpsData = sortBy(data, 'name')
      setFtpData(sortRef.current.descending ? [...ftpsData].reverse() : ftpsData)
    })
    setUserRole(context.service.auth.getRole())

    const studyConfigHandler = (
      _event: string,
      data: {
        studyConfigData: {
          studyGroup: { mccEnabled: boolean; uuid: string }
          study: { uuid: string }
          environment?: { uuid: string } | null
        }
        selectedFtpId: number
      }
    ) => {
      studyUpdateHandler(data)
    }
    const studyRemovalHandler = (_event: string, data: FtpContext) => {
      studyRemovalHandlerFn(data)
    }
    const reFetchHandler = () => {
      reFetchUpdatedData()
    }

    context.$rootScope.$on('studyConfigUpdateSuccess', studyConfigHandler)
    context.$rootScope.$on('studyRemovalSuccess', studyRemovalHandler)
    context.$rootScope.$on('updateFtpIntegrations', reFetchHandler)
    context.$rootScope.$on('archivedStudy', reFetchHandler)

    return () => {
      // EventSystem handles cleanup internally via $on returning unsubscribe
    }
  }, [])

  useEffect(() => {
    if (removedFtpId !== null) {
      const updateFtpData = ftpData?.filter((element) => element.id !== removedFtpId)
      setFtpData(updateFtpData ?? null)
    }
  }, [removedFtpId])

  useEffect(() => {
    filterFtpData()
  }, [studyStateFilter, ftpData])

  const studyUpdateHandler = ({
    studyConfigData: studyConfig,
    selectedFtpId: ftpId
  }: {
    studyConfigData: {
      studyGroup: { mccEnabled: boolean; uuid: string }
      study: { uuid: string }
      environment?: { uuid: string } | null
    }
    selectedFtpId: number
  }) => {
    transferMedia(ftpId, {
      mccEnabled: studyConfig.studyGroup.mccEnabled,
      studyGroup: studyConfig.studyGroup.uuid,
      study: studyConfig.study.uuid,
      environment: studyConfig.environment ? studyConfig.environment.uuid : null
    })
  }

  const studyRemovalHandlerFn = (ftpConfigData: FtpContext) => {
    setRemovedFtpId(ftpConfigData.id)
  }

  const doClose = () => {
    setIsOpen(false)
  }

  const changeSorting = (column: string) => {
    sortColumn(column)
  }

  const sortColumn = (column: string) => {
    const sortData: SortState = { ...sort }
    sortData.column = column
    sortData.descending = !sort.descending
    setSort(sortData)
    sortRef.current = sortData

    const ftpsData = sortBy(
      ftpData,
      (i: FtpContext) => ((i as unknown as Record<string, unknown>)[column] as string)?.toLowerCase?.() ?? ''
    )

    setFtpData(sortData.descending ? [...ftpsData].reverse() : ftpsData)
  }

  const handleStudySelection = (ftpId: number) => {
    setSelectedFtpId(ftpId)
    checkIsStudyIsArchived(ftpId)
  }

  const checkIsStudyIsArchived = (ftpId: number) => {
    const selectedFtp = ftpData?.find((ftp) => ftp.id === ftpId)
    if (selectedFtp) {
      setSelectedFtpData(selectedFtp)
    }
  }

  const transfer = (selectedId?: number) => {
    const id = selectedFtpId ?? selectedId
    const selectedFtp = ftpData?.find((ftp) => ftp.id === id)

    if (!selectedFtp) return

    context.service.eosService!.loadStudyInfo(selectedFtp.id!, null).then(
      (receivedStudyInfo: { studyGroup: string; study: string; environment: string | null; mccEnabled?: boolean }) => {
        if (receivedStudyInfo) {
          transferMedia(selectedFtp.id!, receivedStudyInfo)
        }
      },
      () => {
        // if study details have not been set - ask user to set them
        context.service.toast.warning('EOS_TRANSFER_MEDIA_STUDY_DETAILS_MUST_BE_SET_MSG')
        showModal(<EosStudyInfoDialog ftpId={selectedFtp.id!} />, { keepParentModalsOpen: true })
      }
    )
  }

  const transferMedia = (
    id: number,
    studyInfo: { studyGroup: string; study: string; environment: string | null; mccEnabled?: boolean }
  ) => {
    return context.api.eos
      .startEosTransfer(id, {
        mccEnabled: !!studyInfo.environment,
        studyGroupUuid: studyInfo.studyGroup,
        studyUuid: studyInfo.study,
        studyEnvUuid: studyInfo.environment ?? undefined
      })
      .then(
        () => {
          context.service.toast.success(
            translate(isPdrFlow ? 'PDR_MEDIA_TRANSFER_SUCCESS_TOAST' : 'EOS_MEDIA_TRANSFER_SUCCESS_TOAST')
          )
          doClose()
        },
        (data) => {
          context.service.toast.error(data?.body?.message ?? data?.data?.message ?? 'ERROR')
        }
      )
  }

  const reFetchUpdatedData = () => {
    context.api.settings.getFtpContextsForPicker(true).then((_data) => {
      const data = _data as FtpContext[]
      const ftpsData = sortBy(data, 'name')
      setFtpData(sortRef.current.descending ? [...ftpsData].reverse() : ftpsData)
    })
  }

  const filterListHandler = (selection: StudyStateFilter) => {
    setStudyStateFilter(selection)
  }

  const filterFtpData = () => {
    if (ftpData?.length) {
      if (!isPdrFlow) {
        let ftpDataLocal = [...ftpData]
        ftpDataLocal = ftpDataLocal.filter((item) => item?.contentType !== 'pdr')
        // Reset radio selection via state (no jQuery)
        setSelectedFtpId(null)

        switch (studyStateFilter) {
          case STUDY_STATES.active:
            ftpDataLocal = ftpDataLocal.filter((item) => !item.archived)
            break
          case STUDY_STATES.archived:
            ftpDataLocal = ftpDataLocal.filter((item) => item.archived)
            break
          case STUDY_STATES.all:
            setFilteredFtpData(ftpDataLocal)
            return
        }
        setFilteredFtpData(ftpDataLocal)
      } else {
        let ftpDataLocal = [...ftpData]
        ftpDataLocal = ftpDataLocal.filter((item) => item?.contentType === 'pdr')
        setFilteredFtpData(ftpDataLocal)
      }
    }
  }

  const radioResetKey = `${studyStateFilter}-${filteredFtpData.length}`

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      title={translate(
        isPdrFlow ? 'PDR_MEDIA_TRANSFER_EXISTING_PDR_STUDY_TITLE' : 'EOS_MEDIA_TRANSFER_EXISTING_EOS_STUDY_TITLE'
      )}
      className="existing-study-transfer-modal base-modal"
      size="lg"
    >
      <Modal.Body>
        {!isPdrFlow && (
          <div className="study-status-filter-wrapper">
            <div className="align-right align-bottom d-flex">
              <FilterTrigger noPersist={true} />
            </div>
            <EosTransferMediaStudyStatusFilter filterList={filterListHandler} />
          </div>
        )}
        {!isPdrFlow && (
          <h4 className="form-headline2 label-color">{translate('EOS_MEDIA_SELECT_EXISTING_EOS_STUDY_LABEL')}</h4>
        )}
        <div className="table-responsive">
          <table className="styled-table chita-table">
            <thead>
              <tr className="settings">
                {headers.map((item, index) => (
                  <th key={index} className={item?.className}>
                    <span className="font-bold cursor-pointer" onClick={() => changeSorting(item.column)}>
                      {translate(item.text)}
                    </span>
                    <span
                      className={`sort-caret fa ${sort.column !== item.column ? 'visibility-h' : ''} ${
                        sort.descending ? 'fa-caret-down' : 'fa-caret-up'
                      }`}
                    ></span>
                  </th>
                ))}
                <th className="actions fs12-imp">{translate('ACTIONS')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredFtpData?.length ? (
                filteredFtpData.map((ftp, index) => (
                  <tr key={index}>
                    <td className="clipped-cell">
                      <div className="name-container">
                        {!isPdrFlow && (
                          <input
                            key={radioResetKey}
                            name="ftpSelection"
                            type="radio"
                            className="study-selection-radio-button"
                            onClick={() => handleStudySelection(ftp.id!)}
                            onChange={() => {}}
                          />
                        )}
                        <label className="fs12">{ftp.name}</label>
                      </div>
                    </td>
                    <td className="clipped-cell">
                      <div className="ellipsis fs12">{ftp?.archived ? '-' : ftp.remoteFolderName}</div>
                    </td>
                    <td className="clipped-cell">
                      <div className="ellipsis fs12">{ftp.targetFolderName}</div>
                    </td>
                    <td className="actions">
                      <TableMenu
                        item={ftp}
                        isPdfgenStudy={PDF_GEN_SUBSTRINGS.some((substring) =>
                          ftp?.remoteFolderName?.includes(substring)
                        )}
                        userRole={userRole}
                        isPdrFlow={isPdrFlow}
                        reTransfer={(id: number) => transfer(id)}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>{Array.isArray(ftpData) && <td colSpan={4}>{translate('NO_STUDIES_MESSAGE')}</td>}</tr>
              )}
            </tbody>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Button variant="link" onClick={doClose}>
          {translate('CANCEL')}
        </Modal.Footer.Button>
        {!isPdrFlow && (
          <Button
            disabled={selectedFtpId === null || !!selectedFtpData?.archived}
            data-action="confirm"
            className="btn btn-primary text-capitalize"
            id="2aef97ba-0d68-4160-9464-f64f1219948a"
            onClick={() => transfer()}
          >
            {translate('EOS_MEDIA_TRANSFER_CONTENT_NOW_BTN')}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default EosTransferMediaExistingFtpsModal
